import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { api } from "../api/client"
import { useTheme } from "../context/ThemeContext"
import { useLanguage } from "../context/LanguageContext"
import { useSubscription } from "../context/SubscriptionContext"
import { billingEnabled } from "../config/features"
import ParticleBackground from "../components/ParticleBackground"
import CosmicGatewayScene from "../components/CosmicGatewayScene"

const SUBSCRIPTION_STATUS_LABELS = {
  active: "Active",
  trialing: "Trial",
  past_due: "Payment overdue",
  canceled: "Canceled",
  incomplete: "Incomplete",
  inactive: "No active plan",
}

const FOCUS_OPTIONS = [
  { key: "balanced", labelKey: "focusAreaBalanced", hintKey: "focusAreaBalancedHint" },
  { key: "reading", labelKey: "reading", hintKey: "focusAreaReadingHint" },
  { key: "writing", labelKey: "writing", hintKey: "focusAreaWritingHint" },
  { key: "listening", labelKey: "listening", hintKey: "focusAreaListeningHint" },
  { key: "speaking", labelKey: "speaking", hintKey: "focusAreaSpeakingHint" },
  { key: "grammar", labelKey: "grammar", hintKey: "focusAreaGrammarHint" },
]

export default function Settings() {
  const { theme, setLightTheme, setDarkTheme, isDark, fontSize, setFontSize } =
    useTheme()
  const { language, changeLanguage, t, availableLanguages } = useLanguage()
  const {
    isPro,
    status: subStatus,
    currentPeriodEnd,
    loading: subLoading,
    refresh: refreshSubscription,
  } = useSubscription()
  const [billingWorking, setBillingWorking] = useState(false)
  const [billingError, setBillingError] = useState("")
  const shapesRef = useRef(null)
  const [focusArea, setFocusArea] = useState("balanced")
  const [dailyGoalMinutes, setDailyGoalMinutes] = useState(30)
  const [prefsLoading, setPrefsLoading] = useState(true)
  const [prefsSaving, setPrefsSaving] = useState(false)
  const [prefsError, setPrefsError] = useState("")
  const [prefsSuccess, setPrefsSuccess] = useState("")

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -3}deg) rotateY(${x * 5}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  useEffect(() => {
    let mounted = true
    setPrefsLoading(true)
    setPrefsError("")
    api
      .profile()
      .then((p) => {
        if (!mounted) return
        setFocusArea((p.focus_area || "balanced").toLowerCase())
        setDailyGoalMinutes(
          typeof p.daily_goal_minutes === "number" ? p.daily_goal_minutes : 30
        )
      })
      .catch((e) => mounted && setPrefsError(e.message))
      .finally(() => mounted && setPrefsLoading(false))
    return () => {
      mounted = false
    }
  }, [])

  const saveLearningPrefs = async (next = {}) => {
    const payload = {
      focus_area: next.focus_area ?? focusArea,
      daily_goal_minutes: next.daily_goal_minutes ?? dailyGoalMinutes,
    }
    setPrefsSaving(true)
    setPrefsError("")
    setPrefsSuccess("")
    try {
      const updated = await api.updateProfile(payload)
      setFocusArea((updated.focus_area || payload.focus_area).toLowerCase())
      setDailyGoalMinutes(
        typeof updated.daily_goal_minutes === "number"
          ? updated.daily_goal_minutes
          : payload.daily_goal_minutes
      )
      setPrefsSuccess(t("focusAreaSaved"))
      setTimeout(() => setPrefsSuccess(""), 2500)
    } catch (e) {
      setPrefsError(e.message)
    } finally {
      setPrefsSaving(false)
    }
  }

  const handleManageBilling = async () => {
    setBillingError("")
    setBillingWorking(true)
    try {
      const res = await api.billingPortal()
      window.location.href = res.url
    } catch (e) {
      setBillingError(e.message || "Could not open the billing portal. Please try again.")
      setBillingWorking(false)
    }
  }

  const subStatusLabel =
    SUBSCRIPTION_STATUS_LABELS[subStatus] || SUBSCRIPTION_STATUS_LABELS.inactive
  const periodEndLabel = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null

  const themeOptions = [
    { key: "light", label: t("lightMode"), onSelect: setLightTheme, active: theme === "light" },
    { key: "dark", label: t("darkMode"), onSelect: setDarkTheme, active: theme === "dark" },
  ]

  const fontSizeOptions = [
    { key: "small", label: t("fontSizeSmall") },
    { key: "medium", label: t("fontSizeMedium") },
    { key: "large", label: t("fontSizeLarge") },
  ]

  const fontSizeLabel =
    fontSizeOptions.find((opt) => opt.key === fontSize)?.label || t("fontSizeMedium")

  const currentLanguageName =
    availableLanguages.find((lang) => lang.code === language)?.name || t("english")

  const focusLabel =
    FOCUS_OPTIONS.find((o) => o.key === focusArea)?.labelKey || "focusAreaBalanced"

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <ParticleBackground particleCount={40} className="z-0 opacity-28 dark:opacity-18" />
      <CosmicGatewayScene layerRef={shapesRef} />
      <div className="profile-ambient" aria-hidden />

      <div className="relative z-10 settings-scene">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-8 lg:py-10 w-full min-w-0 pb-10">
          <header className="settings-hero-minimal mb-6 md:mb-8">
            <h1 className="settings-hero-title">{t("settings")}</h1>
            <div className="settings-hero-accent" aria-hidden />
            <p className="settings-hero-subtitle">{t("settingsSubtitle")}</p>
          </header>

          <div className="settings-summary-strip mb-6 md:mb-8">
            {billingEnabled && (
              <div className="settings-summary-chip">
                <span className="settings-summary-label">Plan</span>
                <span className="settings-summary-value">
                  {subLoading ? "…" : isPro ? "Pro" : "Free"}
                </span>
              </div>
            )}
            <div className="settings-summary-chip">
              <span className="settings-summary-label">{t("theme")}</span>
              <span className="settings-summary-value">
                {isDark ? t("darkMode") : t("lightMode")}
              </span>
            </div>
            <div className="settings-summary-chip">
              <span className="settings-summary-label">{t("fontSize")}</span>
              <span className="settings-summary-value">{fontSizeLabel}</span>
            </div>
            <div className="settings-summary-chip">
              <span className="settings-summary-label">{t("language")}</span>
              <span className="settings-summary-value">{currentLanguageName}</span>
            </div>
            <div className="settings-summary-chip">
              <span className="settings-summary-label">{t("focusArea")}</span>
              <span className="settings-summary-value">{t(focusLabel)}</span>
            </div>
          </div>

          {(prefsError || prefsSuccess) && (
            <div
              className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                prefsError
                  ? "border-destructive/30 bg-destructive/10 text-destructive"
                  : "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
              }`}
            >
              {prefsError || prefsSuccess}
            </div>
          )}

          <div className="space-y-6 md:space-y-8">
            {billingEnabled && (
            <section className="profile-panel-3d profile-section-card settings-section-card p-5 sm:p-6 md:p-8 futuristic-nav-border w-full min-w-0">
              <h2 className="profile-section-title">Subscription</h2>
              <p className="profile-section-desc mb-6">Your current plan and billing.</p>

              <div className="settings-status-row flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-muted-foreground">Current plan</span>
                <span className="text-sm font-medium text-foreground">
                  {subLoading ? (
                    "Checking…"
                  ) : isPro ? (
                    <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary to-accent px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-primary-foreground">
                      Pro
                    </span>
                  ) : (
                    "Free"
                  )}
                </span>
              </div>

              <div className="settings-status-row mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="text-sm font-medium text-foreground">{subStatusLabel}</span>
              </div>

              {periodEndLabel && (
                <div className="settings-status-row mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                  <span className="text-sm text-muted-foreground">
                    {subStatus === "canceled" ? "Access until" : "Renews on"}
                  </span>
                  <span className="text-sm font-medium text-foreground">{periodEndLabel}</span>
                </div>
              )}

              {billingError && (
                <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {billingError}
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-3">
                {isPro && (
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={billingWorking}
                    className="button-glow rounded-xl bg-gradient-to-r from-primary to-accent px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {billingWorking ? "Opening…" : "Manage subscription"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={refreshSubscription}
                  disabled={subLoading}
                  className="rounded-xl border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {subLoading ? "Refreshing…" : "Refresh status"}
                </button>
              </div>
            </section>
            )}

            <section className="profile-panel-3d profile-section-card settings-section-card p-5 sm:p-6 md:p-8 futuristic-nav-border w-full min-w-0">
              <h2 className="profile-section-title">{t("focusArea")}</h2>
              <p className="profile-section-desc mb-6">{t("focusAreaDescription")}</p>

              {prefsLoading ? (
                <p className="text-sm text-muted-foreground">{t("loading")}</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {FOCUS_OPTIONS.map((opt) => {
                      const isActive = focusArea === opt.key
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          disabled={prefsSaving}
                          onClick={() => {
                            setFocusArea(opt.key)
                            saveLearningPrefs({ focus_area: opt.key })
                          }}
                          aria-pressed={isActive}
                          className={`settings-option-card w-full min-w-0 text-left ${
                            isActive ? "settings-option-card--active" : ""
                          }`}
                        >
                          <span className="settings-option-label block">{t(opt.labelKey)}</span>
                          <span className="settings-option-code">{t(opt.hintKey)}</span>
                          {isActive && (
                            <span className="settings-option-check" aria-hidden>
                              ✓
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>

                  <div className="mt-6 space-y-3">
                    <label
                      htmlFor="settings-daily-goal"
                      className="block text-sm font-semibold text-foreground"
                    >
                      {t("dailyGoal")}{" "}
                      <span className="font-normal text-muted-foreground">({t("minutes")})</span>
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                      <input
                        id="settings-daily-goal"
                        type="number"
                        min={5}
                        max={480}
                        step={5}
                        value={dailyGoalMinutes}
                        disabled={prefsSaving}
                        onChange={(e) => setDailyGoalMinutes(Number(e.target.value))}
                        className="profile-input sm:max-w-[10rem]"
                      />
                      <button
                        type="button"
                        disabled={prefsSaving}
                        onClick={() =>
                          saveLearningPrefs({
                            daily_goal_minutes: Math.max(
                              5,
                              Math.min(480, Number(dailyGoalMinutes) || 30)
                            ),
                          })
                        }
                        className="button-glow rounded-xl bg-gradient-to-r from-primary to-accent px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                      >
                        {prefsSaving ? t("saving") : t("saveDailyGoal")}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("dailyGoalSettingsHint")}</p>
                  </div>

                  <div className="mt-4">
                    <Link
                      to="/dashboard"
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {t("viewDashboard")} →
                    </Link>
                  </div>
                </>
              )}
            </section>

            <section className="profile-panel-3d profile-section-card settings-section-card p-5 sm:p-6 md:p-8 futuristic-nav-border w-full min-w-0">
              <h2 className="profile-section-title">{t("appearance")}</h2>
              <p className="profile-section-desc mb-6">{t("theme")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {themeOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={opt.onSelect}
                    aria-pressed={opt.active}
                    className={`settings-option-card w-full min-w-0 ${
                      opt.active ? "settings-option-card--active" : ""
                    }`}
                  >
                    <span className="settings-option-label">{opt.label}</span>
                    {opt.active && <span className="settings-option-check" aria-hidden>✓</span>}
                  </button>
                ))}
              </div>

              <div className="settings-status-row mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("currentTheme")}</span>
                <span className="text-sm font-medium text-foreground">
                  {isDark ? t("darkMode") : t("lightMode")}
                </span>
              </div>
            </section>

            <section
              className="profile-panel-3d profile-section-card settings-section-card p-5 sm:p-6 md:p-8 w-full min-w-0"
              style={{ animationDelay: "0.06s" }}
            >
              <h2 className="profile-section-title">{t("fontSize")}</h2>
              <p className="profile-section-desc mb-6">{t("fontSizeDescription")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {fontSizeOptions.map((opt) => {
                  const isActive = fontSize === opt.key
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setFontSize(opt.key)}
                      aria-pressed={isActive}
                      className={`settings-option-card w-full min-w-0 ${
                        isActive ? "settings-option-card--active" : ""
                      }`}
                    >
                      <span
                        className="settings-option-label block"
                        style={{
                          fontSize:
                            opt.key === "small" ? "14px" : opt.key === "large" ? "20px" : "16px",
                        }}
                      >
                        {opt.label}
                      </span>
                      {isActive && <span className="settings-option-check" aria-hidden>✓</span>}
                    </button>
                  )
                })}
              </div>

              <div className="settings-status-row mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("currentFontSize")}</span>
                <span className="text-sm font-medium text-foreground">{fontSizeLabel}</span>
              </div>
            </section>

            <section
              className="profile-panel-3d profile-section-card settings-section-card p-5 sm:p-6 md:p-8 w-full min-w-0"
              style={{ animationDelay: "0.08s" }}
            >
              <h2 className="profile-section-title">{t("language")}</h2>
              <p className="profile-section-desc mb-6">{t("selectLanguage")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {availableLanguages.map((lang) => {
                  const isActive = language === lang.code
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => changeLanguage(lang.code)}
                      aria-pressed={isActive}
                      className={`settings-option-card w-full min-w-0 text-left sm:text-center ${
                        isActive ? "settings-option-card--active" : ""
                      }`}
                    >
                      <span className="settings-option-label block">{lang.name}</span>
                      <span className="settings-option-code">{lang.code}</span>
                      {isActive && <span className="settings-option-check" aria-hidden>✓</span>}
                    </button>
                  )
                })}
              </div>

              <div className="settings-status-row mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("currentLanguage")}</span>
                <span className="text-sm font-medium text-foreground">{currentLanguageName}</span>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
